<template>
  <div class="home">
    <!-- Header -->
    <header class="home-header">
      <div class="hi-wrap">
        <div class="hi-title">Привет, {{ userName }} 👋</div>
        <div class="hi-sub">Ваш VPN под контролем</div>
      </div>
      <button class="avatar-btn" @click="goTo('profile')" aria-label="Профиль">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="#0F1512" stroke-width="1.9"/><path d="M4 20c0-3.6 3.6-6 8-6s8 2.4 8 6" stroke="#0F1512" stroke-width="1.9" stroke-linecap="round"/></svg>
      </button>
    </header>

    <!-- Balance -->
    <div class="balance-card">
      <div class="balance-glow"></div>
      <div class="balance-top">
        <span class="balance-label">Баланс</span>
        <button class="history-btn" @click="handlePaymentHistoryClick" aria-label="История">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M8 6h11M8 12h11M8 18h11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="4" cy="6" r="1.3" fill="currentColor"/><circle cx="4" cy="12" r="1.3" fill="currentColor"/><circle cx="4" cy="18" r="1.3" fill="currentColor"/></svg>
        </button>
      </div>
      <div class="balance-amount" :class="{ skeleton: balance === null && !hasSessionData }">
        {{ balance !== null ? balance : 0 }} <span class="rub">₽</span>
      </div>
      <button class="balance-cta" @click="handleDepositClick">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="3" stroke="currentColor" stroke-width="2"/><path d="M2 10h20" stroke="currentColor" stroke-width="2"/></svg>
        Пополнить баланс
      </button>
    </div>

    <!-- Promo banners -->
    <div class="banners-section" v-if="banners.length">
      <div
        class="banners-container"
        ref="bannersContainer"
        @touchstart="handleBannerTouchStart"
        @touchmove="handleBannerTouchMove"
        @touchend="handleBannerTouchEnd"
      >
        <div class="banners-track" :style="{ transform: `translateX(${bannerOffset}px)` }">
          <div
            v-for="(banner, index) in banners"
            :key="index"
            class="banner-item"
            @click="handleBannerClick(banner)"
          >
            <img :src="banner.image" :alt="banner.title" class="banner-image" />
          </div>
        </div>
        <div class="banner-dots">
          <div
            v-for="(banner, index) in banners"
            :key="index"
            :class="['dot', { active: currentBannerIndex === index }]"
            @click.stop="goToBanner(index)"
          ></div>
        </div>
      </div>
    </div>

    <!-- Subscriptions -->
    <div v-if="isInitialLoading" class="sub-card sub-skeleton"></div>

    <template v-else>
      <!-- Free tariff -->
      <div v-if="isFreeTariffAvailable" class="sub-card" ref="freeTariffRef">
        <div class="sub-inner">
          <div class="sub-row">
            <div class="sub-title">{{ freeTariff.tariff_name }}</div>
            <span class="badge-active">Активна</span>
          </div>
          <div class="chips">
            <span class="chip">🌍 Базовые страны</span>
            <span class="chip">♾️ Безлимит</span>
          </div>
          <div class="progress-wrap">
            <div class="progress-track"><div class="progress-fill" :style="{ width: trafficPercentage + '%' }"></div></div>
            <div class="progress-label">{{ freeTariff.tariff_left }}</div>
          </div>
          <div class="sub-actions">
            <button class="btn-connect" ref="connectButtonRef" @click="handleConnectionClick('FreeTariff')">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12h6M12 9v6" stroke="#fff" stroke-width="2" stroke-linecap="round"/><path d="M10 6.5l1.3-1.3a3.5 3.5 0 0 1 5 5L15 11.5M14 17.5l-1.3 1.3a3.5 3.5 0 0 1-5-5L9 12.5" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg>
              Подключить
            </button>
          </div>
        </div>
      </div>

      <!-- Paid tariffs -->
      <div v-for="(tariff, index) in uniqueTariffs" :key="index" class="sub-card">
        <div class="sub-inner">
          <div class="sub-row">
            <div class="sub-title">{{ tariff.name }}</div>
            <span class="badge-active" :class="{ expired: tariff.daysRemaining <= 0 }">
              {{ tariff.daysRemaining > 0 ? 'Активна' : 'Истекла' }}
            </span>
          </div>
          <div class="chips">
            <span class="chip">🌍 {{ getCountriesText(tariff.name) }}</span>
            <span class="chip">♾️ Безлимит</span>
            <span class="chip">🖥 {{ getDevicesText(tariff.name) }}</span>
          </div>
          <div v-if="tariff.daysRemaining > 0" class="progress-wrap">
            <div class="progress-track"><div class="progress-fill" :style="{ width: calculateProgress(tariff.daysRemaining) + '%' }"></div></div>
            <div class="progress-label">{{ tariff.subscriptionEnd }}</div>
          </div>
          <div v-else class="expired-note">⏱ {{ tariff.subscriptionEnd }}</div>

          <div v-if="tariff.daysRemaining > 0" class="sub-actions">
            <button class="btn-connect" @click="handleConnectionClick(tariff.name)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12h6M12 9v6" stroke="#fff" stroke-width="2" stroke-linecap="round"/><path d="M10 6.5l1.3-1.3a3.5 3.5 0 0 1 5 5L15 11.5M14 17.5l-1.3 1.3a3.5 3.5 0 0 1-5-5L9 12.5" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg>
              Подключить
            </button>
            <button class="btn-devices" @click="openDevicesModal(tariff.name)">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="6" y="2" width="12" height="20" rx="3" stroke="#0F1512" stroke-width="1.9"/><path d="M10 18.5h4" stroke="#0F1512" stroke-width="1.9" stroke-linecap="round"/></svg>
              {{ deviceCountFor(tariff.name) || 0 }}
            </button>
          </div>

          <button
            v-if="tariff.daysRemaining > 0"
            class="btn-upgrade"
            :class="{ max: !nextTariffName(tariff.name) }"
            @click="openUpgrade(tariff)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 20V6M6 12l6-6 6 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            {{ nextTariffName(tariff.name) ? 'Улучшить до ' + nextTariffName(tariff.name) : 'Максимальный тариф' }}
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!isFreeTariffAvailable && uniqueTariffs.length === 0" class="empty-state">
        <div class="empty-icon">📦</div>
        <div class="empty-title">Нет активных подписок</div>
        <div class="empty-text">Выберите тариф и начните пользоваться VPN</div>
        <button class="btn-buy" @click="handleBuySubscriptionClick">Купить подписку</button>
      </div>
    </template>

    <div class="legal-links">
      <a href="https://tma.avevpn.com/privacy_policy_page.html" target="_blank" rel="noopener noreferrer">Политика конфиденциальности</a>
      <span class="separator">•</span>
      <a href="https://tma.avevpn.com/user_agreement_avevpn.html" target="_blank" rel="noopener noreferrer">Соглашение</a>
    </div>

    <!-- Welcome Popup -->
    <WelcomePopup :is-visible="showWelcomePopup" @close="closeWelcomePopup" @open-tasks="openTasksFromPopup" />

    <!-- Onboarding Tour -->
    <Teleport to="body">
      <div v-if="showOnboardingTour" class="onboarding-overlay" @click="skipTour">
        <div class="onboarding-spotlight" :style="spotlightStyle"></div>
        <div class="onboarding-tooltip" :style="tooltipStyle">
          <div class="tooltip-content">
            <div class="tooltip-icon">👆</div>
            <div class="tooltip-title">Нажмите «Подключить»</div>
            <div class="tooltip-text">Активируйте свой бесплатный пробный период прямо сейчас</div>
            <button class="tooltip-btn" @click.stop="skipTour">Понятно</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Devices sheet -->
    <Teleport to="body">
      <div v-if="showDevicesModal" class="sheet-overlay" @click.self="closeDevicesModal">
        <div class="sheet">
          <div class="sheet-handle"></div>
          <div class="sheet-head">
            <div class="sheet-title">📱 Устройства · {{ selectedTariffDisplayName }}</div>
            <button class="sheet-close" @click="closeDevicesModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
            </button>
          </div>

          <div v-if="isLoadingDevices && selectedDevices.length === 0" class="devices-loading">
            <div class="spinner"></div><span>Загрузка устройств…</span>
          </div>
          <div v-else-if="selectedDevices.length === 0" class="devices-empty">
            <div class="empty-icon">📭</div><div>Нет активных устройств</div>
          </div>
          <div v-else class="devices-list">
            <div v-for="device in selectedDevices" :key="device.hwid + device.userUuid" class="device-card">
              <div class="device-icon">{{ getPlatformIcon(device.platform) }}</div>
              <div class="device-info">
                <div class="device-model">{{ device.deviceModel || 'Устройство' }}</div>
                <div class="device-meta">{{ device.platform }} {{ device.osVersion }} · {{ getAppName(device.userAgent) }} · {{ formatDeviceDate(device.updatedAt) }}</div>
              </div>
              <div class="device-actions">
                <template v-if="confirmDeleteHwid === device.hwid">
                  <button class="dev-yes" :disabled="deletingHwid === device.hwid" @click="deleteDevice(device.hwid)">
                    <span v-if="deletingHwid === device.hwid" class="spinner-sm"></span><span v-else>✓</span>
                  </button>
                  <button class="dev-no" @click="cancelDelete">✕</button>
                </template>
                <button v-else class="dev-del" @click="requestDelete(device.hwid)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              </div>
            </div>
          </div>
          <div class="sheet-hint">Удалённое устройство потеряет доступ к VPN до повторного подключения</div>
        </div>
      </div>
    </Teleport>

    <!-- Upgrade sheet -->
    <Teleport to="body">
      <div v-if="upgradeOpen" class="sheet-overlay" @click.self="closeUpgrade">
        <div class="sheet">
          <div class="sheet-handle"></div>
          <div class="sheet-title">Улучшить тариф</div>
          <div class="sheet-sub">Доплата рассчитывается пропорционально остатку · {{ upgradeRemainingDays }} дней</div>

          <div class="up-current">
            <span class="up-emoji">{{ emojiByKey(upgradeFromKey) }}</span>
            <div class="up-current-info">
              <div class="up-label">Текущий тариф</div>
              <div class="up-name">{{ upgradeFromKey }}</div>
            </div>
            <span class="up-arrow">→</span>
          </div>

          <button class="up-option selected">
            <span class="up-emoji">{{ emojiByKey(upgradeToKey) }}</span>
            <div class="up-option-info">
              <div class="up-option-name">{{ upgradeToKey }}</div>
              <div class="up-option-desc">Больше стран, устройств и скорости</div>
            </div>
            <div class="up-option-price">
              <div class="up-surcharge">+{{ upgradeProration.base }} ₽</div>
              <div class="up-surcharge-label">доплата</div>
            </div>
          </button>

          <div class="up-bypass" @click="toggleUpgradeBypass">
            <span class="up-emoji">🛡️</span>
            <div class="up-option-info">
              <div class="up-option-name sm">Обход белых списков</div>
              <div class="up-option-desc">+{{ upgradeBypassSurcharge }} ₽ к доплате</div>
            </div>
            <div class="toggle" :class="{ on: upgradeBypass }"><span class="knob"></span></div>
          </div>

          <div class="up-summary">
            <div class="up-summary-row">
              <span>Доплата с баланса</span>
              <b>{{ upgradeProration.total }} ₽</b>
            </div>
            <div class="up-summary-row muted">
              <span>Баланс после</span>
              <b :class="{ danger: !upgradeCanAfford }">{{ upgradeAfterBalance }} ₽</b>
            </div>
          </div>

          <button
            class="up-cta"
            :class="{ disabled: !upgradeCanAfford || upgradeLoading }"
            :disabled="!upgradeCanAfford || upgradeLoading"
            @click="confirmUpgrade"
          >
            <span v-if="upgradeLoading" class="spinner-sm"></span>
            <span v-else>{{ upgradeCanAfford ? 'Улучшить · ' + upgradeProration.total + ' ₽' : 'Недостаточно средств' }}</span>
          </button>
        </div>
      </div>
    </Teleport>

    <!-- Toast -->
    <Teleport to="body">
      <div v-if="toast.show" class="toast">{{ toast.msg }}</div>
    </Teleport>
  </div>
</template>

<script setup>
import {ref, onMounted, onUnmounted, computed, watch, nextTick} from "vue";
import {useRouter, useRoute} from "vue-router";
import {haptic, disableSwipes} from "@/utils/telegram";
import WelcomePopup from "@/components/WelcomePopup.vue";
import { fetchAndCacheUserInfo } from '@/services/userService';

const router = useRouter();
const route = useRoute();

const balance = ref(null);
const freeTariff = ref(null);
const uniqueTariffs = ref([]);
const showWelcomePopup = ref(false);
const showOnboardingTour = ref(false);
const botName = ref("Prometei");

// ✅ Новые переменные для табов и белых списков
const activeTab = ref('subscriptions'); // 'subscriptions' или 'whitelist'
const whiteList = ref(null);
const shouldAnimateWhitelist = ref(false);
const whitelistProgress = ref(0);

const hasSessionData = ref(false);
const isInitialLoading = ref(true);
const isRefreshing = ref(false);

// Refs для элементов
const connectButtonRef = ref(null);
const freeTariffRef = ref(null);

// Notification swipe state
const isNotificationDismissed = ref(false);
const swipeOffset = ref(0);
const notificationOpacity = ref(1);
const touchStartX = ref(0);
const notificationRef = ref(null);

// Banner swipe state
const currentBannerIndex = ref(0);
const bannerOffset = ref(0);
const bannerTouchStartX = ref(0);
const bannerTouchCurrentX = ref(0);
const isBannerSwiping = ref(false);
const bannerAutoplayInterval = ref(null);
const bannersContainer = ref(null);

// Onboarding tour state
const spotlightStyle = ref({});
const tooltipStyle = ref({});

// ===== ✅ Управление устройствами (HWID) =====
const API_BASE = 'https://baaack.zabanishumresh.com';

const devices = ref([]);
const isLoadingDevices = ref(false);
const showDevicesModal = ref(false);
const selectedTariffName = ref('');         // 'Basic' | 'Medium' | 'Premium'
const selectedTariffDisplayName = ref('');  // напр. 'Medium 🚀'
const confirmDeleteHwid = ref(null);
const deletingHwid = ref(null);

// Banners data
const banners = ref([
  {
    id: 1,
    image: '/tg_banner.png',
    title: 'АКЦИЯ',
    route: 'buyVPN'
  },
  {
    id: 2,
    image: '/banner_ref.png',
    title: 'Реферальная программа',
    route: 'referal'
  },
  {
    id: 3,
    image: '/tasks_banner.png',
    title: 'Задания',
    route: 'tasks'
  }
]);

const isFreeTariffAvailable = computed(() => {
  return freeTariff.value && freeTariff.value.tariff_name;
});

const trafficPercentage = computed(() => {
  if (!freeTariff.value) return 0;

  const totalDays = 3;
  const totalHours = totalDays * 24;
  const tariffLeftString = freeTariff.value.tariff_left;

  const parts = tariffLeftString.split(' ');
  if (parts.length < 4) return 0;

  const daysLeft = parseInt(parts[0], 10) || 0;
  const hoursLeft = parseInt(parts[2], 10) || 0;

  const totalLeftHours = daysLeft * 24 + hoursLeft;
  let percentage = (totalLeftHours / totalHours) * 100;
  const buffer = Math.min(percentage * 0.1, 60);

  return Math.max(0, percentage - buffer);
});

const trafficBarClass = computed(() => {
  const daysLeft = parseInt(freeTariff.value?.tariff_left || 0);

  if (daysLeft <= 1) {
    return 'danger';
  } else if (daysLeft === 2) {
    return 'warning';
  } else {
    return 'success';
  }
});

const shouldShowTariffNotification = computed(() => {
  return uniqueTariffs.value.some(tariff => {
    const isExpired = tariff.daysRemaining <= 0;
    const isExpiringSoon = tariff.daysRemaining > 0 && tariff.daysRemaining <= 7;
    return isExpired || isExpiringSoon;
  });
});

const notificationTariff = computed(() => {
  const expired = uniqueTariffs.value.find(t => t.daysRemaining <= 0);
  if (expired) return expired;
  return uniqueTariffs.value.find(t => t.daysRemaining > 0 && t.daysRemaining <= 7);
});

const notificationTitle = computed(() => {
  if (!notificationTariff.value) return '';
  if (notificationTariff.value.daysRemaining <= 0) {
    return 'Тариф истёк';
  }
  return 'Тариф скоро истечёт';
});

const notificationText = computed(() => {
  if (!notificationTariff.value) return '';
  const tariffName = notificationTariff.value.name;
  if (notificationTariff.value.daysRemaining <= 0) {
    return `Тариф "${tariffName}" истёк. Продлите подписку`;
  }
  const days = notificationTariff.value.daysRemaining;
  const daysWord = getDaysWord(days);
  return `Тариф "${tariffName}" истекает через ${days} ${daysWord}`;
});

// ✅ NEW: Проверка нового пользователя с валидацией first_login
const isNewUser = computed(() => {
  const welcomeShown = localStorage.getItem("welcomeShown");

  if (welcomeShown === "true") {
    return false;
  }

  const firstLogin = sessionStorage.getItem("first_login");

  if (!firstLogin) {
    return false;
  }

  const firstLoginTime = parseInt(firstLogin) * 1000;
  const currentTime = Date.now();
  const threeHoursInMs = 3 * 60 * 60 * 1000;

  const isWithinThreeHours = (currentTime - firstLoginTime) <= threeHoursInMs;

  console.log("🔍 Проверка нового пользователя:", {
    welcomeShown,
    firstLogin,
    firstLoginTime: new Date(firstLoginTime),
    currentTime: new Date(currentTime),
    isWithinThreeHours
  });

  return isWithinThreeHours;
});

// ===== ✅ Логика устройств (HWID) =====
const buildAuthHeader = () => {
  const token = sessionStorage.getItem('jwtToken') || '';
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
};

// Тариф устройства по суффиксу _tariff_username
const getDeviceTariff = (device) => {
  const t = device?._tariff_username || '';
  if (t.endsWith('Premium')) return 'Premium';
  if (t.endsWith('Medium')) return 'Medium';
  if (t.endsWith('Basic')) return 'Basic';
  return 'Unknown';
};

// Ключ тарифа из отображаемого имени ('Medium 🚀' -> 'Medium')
const getTariffKey = (displayName) => {
  if (!displayName) return null;
  if (displayName.includes('Premium')) return 'Premium';
  if (displayName.includes('Medium')) return 'Medium';
  if (displayName.includes('Basic')) return 'Basic';
  return null;
};

const deviceCountByTariff = computed(() => {
  const counts = { Basic: 0, Medium: 0, Premium: 0 };
  devices.value.forEach(d => {
    const key = getDeviceTariff(d);
    if (counts[key] !== undefined) counts[key]++;
  });
  return counts;
});

const deviceCountFor = (displayName) => {
  const key = getTariffKey(displayName);
  return key ? deviceCountByTariff.value[key] : 0;
};

const selectedDevices = computed(() => {
  if (!selectedTariffName.value) return [];
  return devices.value.filter(d => getDeviceTariff(d) === selectedTariffName.value);
});

const fetchDevices = async () => {
  const token = sessionStorage.getItem('jwtToken');
  if (!token) {
    console.error('JWT токен не найден для загрузки устройств');
    return;
  }
  isLoadingDevices.value = true;
  try {
    const res = await fetch(`${API_BASE}/hwid/devices`, {
      headers: { accept: 'application/json', Authorization: buildAuthHeader() }
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    devices.value = await res.json();
    console.log('✅ Устройства загружены:', devices.value.length);
  } catch (e) {
    console.error('❌ Ошибка загрузки устройств:', e);
  } finally {
    isLoadingDevices.value = false;
  }
};

const openDevicesModal = (displayName) => {
  haptic.impact();
  const key = getTariffKey(displayName);
  if (!key) return;
  selectedTariffName.value = key;
  selectedTariffDisplayName.value = displayName;
  confirmDeleteHwid.value = null;
  showDevicesModal.value = true;
  sendMetric(`ViewDevices-${key}`);
  fetchDevices(); // обновляем при открытии
};

const closeDevicesModal = () => {
  showDevicesModal.value = false;
  confirmDeleteHwid.value = null;
  selectedTariffName.value = '';
  selectedTariffDisplayName.value = '';
};

const requestDelete = (hwid) => {
  haptic.impact();
  confirmDeleteHwid.value = hwid;
};

const cancelDelete = () => {
  confirmDeleteHwid.value = null;
};

const deleteDevice = async (hwid) => {
  deletingHwid.value = hwid;
  confirmDeleteHwid.value = null;
  try {
    const res = await fetch(`${API_BASE}/hwid/devices/delete`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: buildAuthHeader()
      },
      body: JSON.stringify({ hwid })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    haptic.impact();
    sendMetric(`DeleteDevice-${selectedTariffName.value}`);
    await fetchDevices(); // перечитываем актуальный список с сервера
  } catch (e) {
    console.error('❌ Ошибка удаления устройства:', e);
  } finally {
    deletingHwid.value = null;
  }
};

const getPlatformIcon = (platform) => {
  const p = (platform || '').toLowerCase();
  if (p.includes('ios')) return '📱';
  if (p.includes('mac')) return '💻';
  if (p.includes('win')) return '🖥';
  if (p.includes('android')) return '🤖';
  return '📲';
};

const getAppName = (ua) => (ua ? (ua.split('/')[0] || ua) : 'Неизвестно');

const formatDeviceDate = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch {
    return '';
  }
};

// ✅ Функция переключения табов с анимацией
const switchTab = (tab) => {
  haptic.impact();
  const previousTab = activeTab.value;
  activeTab.value = tab;
  sendMetric(`SwitchTab-${tab}`);
  console.log(`📑 Переключение на вкладку: ${tab}`);

  // Запуск анимации при переключении на белые списки
  if (tab === 'whitelist' && previousTab !== 'whitelist') {
    shouldAnimateWhitelist.value = false;
    whitelistProgress.value = 0;

    nextTick(() => {
      setTimeout(() => {
        shouldAnimateWhitelist.value = true;
        whitelistProgress.value = calculateWhitelistProgress();
      }, 100);
    });
  }
};

// ✅ Расчет прогресса для белых списков
const calculateWhitelistProgress = () => {
  if (!whiteList.value || whiteList.value.total_purchased === 0) return 0;
  return Math.min(100, (whiteList.value.gb_left / whiteList.value.total_purchased) * 100);
};

// ✅ Обработчики для белых списков
const handleWhitelistConnection = () => {
  console.log("🔗 Клик по Подключить белый список");
  sendMetric('Connect-Whitelist');
  goTo('connection');
};

const handleBuyWhitelist = () => {
  console.log("💎 Клик по Купить трафик");
  sendMetric('BuyWhitelist');
  goTo('buyVPN'); // или другой роут для покупки белых списков
};

watch(() => route.path, (newPath, oldPath) => {
  console.log(`📍 Смена маршрута: ${oldPath} → ${newPath}`);

  if (oldPath === '/' && newPath !== '/') {
    if (showWelcomePopup.value) {
      console.log("👋 Закрываем попап при смене вкладки");
      showWelcomePopup.value = false;
      localStorage.setItem("welcomeShown", "true");
    }
    if (showOnboardingTour.value) {
      console.log("🎯 Закрываем тур при смене вкладки");
      showOnboardingTour.value = false;
    }
    // ✅ Закрываем шторку устройств при смене вкладки
    if (showDevicesModal.value) {
      closeDevicesModal();
    }
  }
});

const getCountriesText = (tariffName) => {
  if (tariffName.includes('Basic')) return '4 страны';
  if (tariffName.includes('Medium')) return '8 стран';
  if (tariffName.includes('Premium')) return '12 стран';
  return 'Несколько стран';
};

const getDevicesText = (tariffName) => {
  if (tariffName.includes('Basic')) return '1 устройство';
  if (tariffName.includes('Medium')) return '2 устройства';
  if (tariffName.includes('Premium')) return '10 устройств';
  return '1 устройство';
};

const calculateProgress = (daysRemaining) => {
  const maxDays = 30;
  return Math.min(100, (daysRemaining / maxDays) * 100);
};

const addEmojiToTariff = (tariffName) => {
  if (tariffName.includes('Free')) return 'Free 🌱';
  if (tariffName.includes('Basic')) return 'Basic 🧵';
  if (tariffName.includes('Medium')) return 'Medium 🚀';
  if (tariffName.includes('Premium')) return 'Premium 💎';
  return tariffName;
};

// Banner functions
const getBannerWidth = () => {
  if (!bannersContainer.value) return 0;
  return bannersContainer.value.offsetWidth;
};

const updateBannerOffset = (animated = true) => {
  const width = getBannerWidth();
  const targetOffset = -currentBannerIndex.value * width;

  if (animated) {
    bannerOffset.value = targetOffset;
  } else {
    bannerOffset.value = targetOffset;
  }
};

const handleBannerTouchStart = (e) => {
  stopBannerAutoplay();
  isBannerSwiping.value = true;
  bannerTouchStartX.value = e.touches[0].clientX;
  bannerTouchCurrentX.value = e.touches[0].clientX;
};

const handleBannerTouchMove = (e) => {
  if (!isBannerSwiping.value) return;

  bannerTouchCurrentX.value = e.touches[0].clientX;
  const diff = bannerTouchCurrentX.value - bannerTouchStartX.value;
  const width = getBannerWidth();
  const baseOffset = -currentBannerIndex.value * width;

  bannerOffset.value = baseOffset + diff;
};

const handleBannerTouchEnd = () => {
  if (!isBannerSwiping.value) return;

  const diff = bannerTouchCurrentX.value - bannerTouchStartX.value;
  const width = getBannerWidth();
  const threshold = width * 0.3;

  if (Math.abs(diff) > threshold) {
    if (diff > 0 && currentBannerIndex.value > 0) {
      currentBannerIndex.value--;
    } else if (diff < 0 && currentBannerIndex.value < banners.value.length - 1) {
      currentBannerIndex.value++;
    }
  }

  updateBannerOffset(true);
  isBannerSwiping.value = false;
  startBannerAutoplay();
};

const goToBanner = (index) => {
  stopBannerAutoplay();
  currentBannerIndex.value = index;
  updateBannerOffset(true);
  startBannerAutoplay();
};

const nextBanner = () => {
  currentBannerIndex.value = (currentBannerIndex.value + 1) % banners.value.length;
  updateBannerOffset(true);
};

const startBannerAutoplay = () => {
  stopBannerAutoplay();
  bannerAutoplayInterval.value = setInterval(() => {
    nextBanner();
  }, 5000);
};

const stopBannerAutoplay = () => {
  if (bannerAutoplayInterval.value) {
    clearInterval(bannerAutoplayInterval.value);
    bannerAutoplayInterval.value = null;
  }
};

const handleBannerClick = (banner) => {
  haptic.impact();
  sendMetric(`Banner-${banner.title}`);
  router.push(`/${banner.route}`);
};

const loadDataFromSession = () => {
  const sessionBalance = sessionStorage.getItem("balance");
  const sessionAllTariffs = sessionStorage.getItem("all_tariffs");
  const sessionWhiteList = sessionStorage.getItem("white_list");

  if (sessionBalance !== null) {
    balance.value = parseFloat(sessionBalance);
    hasSessionData.value = true;
  }

  // ✅ Загружаем white_list из sessionStorage
  if (sessionWhiteList) {
    try {
      whiteList.value = JSON.parse(sessionWhiteList);
      console.log("✅ White list загружен из sessionStorage:", whiteList.value);
    } catch (error) {
      console.error("❌ Ошибка при парсинге white_list:", error);
    }
  }

  if (sessionAllTariffs) {
    try {
      const tariffsData = JSON.parse(sessionAllTariffs);
      const tempTariffs = [];

      ['basic', 'medium', 'premium'].forEach(type => {
        if (tariffsData[type]?.expiry) {
          const expiry = parseInt(tariffsData[type].expiry);
          const endDate = new Date(expiry * 1000);
          const timeDifference = endDate - new Date();
          const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

          const tariffName = type.charAt(0).toUpperCase() + type.slice(1);

          tempTariffs.push({
            name: addEmojiToTariff(tariffName),
            expiry: expiry,
            subscription_link: tariffsData[type].link || '',
            daysRemaining: daysRemaining,
            subscriptionEnd: daysRemaining > 0
                ? `Осталось ${daysRemaining} ${getDaysWord(daysRemaining)}`
                : "Подписка истекла"
          });
        }
      });

      uniqueTariffs.value = tempTariffs;

      if (tariffsData.free?.expiry) {
        const freeExpiry = parseInt(tariffsData.free.expiry);
        const currentTime = Math.floor(Date.now() / 1000);
        const timeLeft = freeExpiry - currentTime;

        if (timeLeft > 0) {
          const daysLeft = Math.floor(timeLeft / 86400);
          const hoursLeft = Math.floor((timeLeft % 86400) / 3600);

          freeTariff.value = {
            tariff_name: "Free 🌱",
            tariff_left: `${daysLeft} дней ${hoursLeft} часов`,
            subscription_link: tariffsData.free.link || ''
          };
        }
      }

      hasSessionData.value = true;
    } catch (error) {
      console.error("Ошибка при загрузке тарифов из sessionStorage:", error);
    }
  }

  isInitialLoading.value = false;
};

const refreshDataInBackground = () => {
  console.log("🔄 Запускаем фоновое обновление данных...");
  isRefreshing.value = true;

  fetchAndCacheUserInfo(true)
      .then(() => {
        console.log("✅ Данные обновлены, перезагружаем из sessionStorage");
        loadDataFromSession();
      })
      .catch((error) => {
        console.error("❌ Ошибка при обновлении данных в фоне:", error);
      })
      .finally(() => {
        isRefreshing.value = false;
      });
};

const sendMetric = (buttonName) => {
  try {
    const token = sessionStorage.getItem("jwtToken");
    if (!token) {
      console.error("JWT токен не найден для отправки метрики");
      return;
    }

    const eventname = `Home-${buttonName}-${botName.value}`;

    fetch('https://baaack.zabanishumresh.com/addevent', {
      method: 'POST',
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({eventname}),
      keepalive: true
    })
        .then(response => {
          if (response.ok) {
            console.log(`📊 Метрика отправлена: ${eventname}`);
          } else {
            console.error(`❌ Ошибка отправки метрики: ${response.status}`);
          }
        })
        .catch(error => {
          console.error("Ошибка при отправке метрики:", error);
        });
  } catch (error) {
    console.error("Ошибка при отправке метрики:", error);
  }
};

const goTo = (route) => {
  console.log(`🔗 Переход на маршрут: ${route}`);
  haptic.impact();
  router.push(`/${route}`);
};

const handleDepositClick = () => {
  console.log("💳 Клик по кнопке Пополнить");
  sendMetric('Deposit-btn');
  goTo('deposit');
};

const handlePaymentHistoryClick = () => {
  console.log("📋 Клик по истории платежей");
  sendMetric('PaymentHistory-btn');
  goTo('payment-history');
};

const handleConnectionClick = (tariffName) => {
  console.log(`🔗 Клик по Подключить: ${tariffName}`);
  sendMetric(`Connect-${tariffName}`);

  if (showOnboardingTour.value) {
    skipTour();
  }

  goTo('connection');
};

const handleBuySubscriptionClick = () => {
  console.log("🛒 Клик по Купить подписку");
  sendMetric('BuySubscription');
  goTo('buyVPN');
};

const handleReferralClick = () => {
  console.log("🎁 Клик по Реферальной программе");
  sendMetric('Referral');
  goTo('referal');
};

const handleTasksClick = () => {
  console.log("✨ Клик по Заданиям");
  sendMetric('Tasks');
  goTo('tasks');
};

const handleTouchStart = (e) => {
  touchStartX.value = e.touches[0].clientX;
};

const handleTouchMove = (e) => {
  const currentX = e.touches[0].clientX;
  const diff = currentX - touchStartX.value;

  if (diff < 0) {
    swipeOffset.value = diff;
    notificationOpacity.value = 1 + (diff / 300);
  }
};

const handleTouchEnd = () => {
  if (swipeOffset.value < -100) {
    dismissNotification();
  } else {
    swipeOffset.value = 0;
    notificationOpacity.value = 1;
  }
};

const dismissNotification = () => {
  sendMetric('DismissNotification');
  swipeOffset.value = -500;
  notificationOpacity.value = 0;
  setTimeout(() => {
    isNotificationDismissed.value = true;
  }, 300);
};

const closeWelcomePopup = () => {
  console.log("👋 Закрываем Welcome Popup");
  showWelcomePopup.value = false;
  localStorage.setItem("welcomeShown", "true");
  sendMetric('WelcomePopup-Closed');

  nextTick(() => {
    if (route.path === '/' && isNewUser.value && isFreeTariffAvailable.value) {
      console.log("🎯 Условия для тура выполнены, запускаем через 500ms");
      setTimeout(() => {
        if (route.path === '/') {
          startOnboardingTour();
        }
      }, 500);
    }
  });
};

const openTasksFromPopup = () => {
  showWelcomePopup.value = false;
  localStorage.setItem("welcomeShown", "true");
  sendMetric('WelcomePopup-OpenTasks');
  goTo('tasks');
};

const startOnboardingTour = () => {
  console.log("🎯 Попытка запустить Onboarding Tour...");

  if (!connectButtonRef.value) {
    console.error("❌ connectButtonRef не найден");
    return;
  }

  if (!freeTariffRef.value) {
    console.error("❌ freeTariffRef не найден");
    return;
  }

  console.log("✅ Refs найдены, получаем координаты");

  try {
    const buttonRect = connectButtonRef.value.getBoundingClientRect();
    const cardRect = freeTariffRef.value.getBoundingClientRect();

    console.log("📐 Button rect:", buttonRect);
    console.log("📐 Card rect:", cardRect);

    spotlightStyle.value = {
      top: `${cardRect.top - 8}px`,
      left: `${cardRect.left - 8}px`,
      width: `${cardRect.width + 16}px`,
      height: `${cardRect.height + 16}px`,
    };

    tooltipStyle.value = {
      top: `${buttonRect.bottom + 20}px`,
      left: '50%',
      transform: 'translateX(-50%)',
    };

    console.log("🎨 Spotlight style:", spotlightStyle.value);
    console.log("🎨 Tooltip style:", tooltipStyle.value);

    showOnboardingTour.value = true;
    sendMetric('OnboardingTour-Started');
    console.log("✅ Onboarding Tour запущен!");
  } catch (error) {
    console.error("❌ Ошибка при запуске тура:", error);
  }
};

const skipTour = () => {
  console.log("⏭ Пропускаем тур");
  showOnboardingTour.value = false;
  localStorage.setItem("welcomeShown", "true");
  sendMetric('OnboardingTour-Skipped');
};

watch(isFreeTariffAvailable, (newVal) => {
  console.log("👀 isFreeTariffAvailable изменился:", newVal);

  if (newVal && isNewUser.value && !showWelcomePopup.value && !showOnboardingTour.value && route.path === '/') {
    console.log("🚀 Автозапуск тура через watch");
    nextTick(() => {
      setTimeout(() => {
        if (route.path === '/') {
          startOnboardingTour();
        }
      }, 800);
    });
  }
});

function getDaysWord(days) {
  if (days % 10 === 1 && days % 100 !== 11) {
    return "День";
  } else if (
      [2, 3, 4].includes(days % 10) &&
      ![12, 13, 14].includes(days % 100)
  ) {
    return "Дня";
  } else {
    return "Дней";
  }
}

onMounted(() => {
  console.log("🚀 Home.vue mounted");

  disableSwipes();

  isNotificationDismissed.value = false;
  swipeOffset.value = 0;
  notificationOpacity.value = 1;

  console.log("📦 Загружаем данные из sessionStorage...");
  loadDataFromSession();

  startBannerAutoplay();

  window.addEventListener('resize', () => updateBannerOffset(false));

  const isTelegramWebApp = window.Telegram && window.Telegram.WebApp;

  if (isTelegramWebApp) {
    const webApp = window.Telegram.WebApp;
    const initData = webApp.initData || "";

    if (initData === "") {
      router.push('/welcome');
      return;
    }

    const telegramId = webApp.initDataUnsafe.user?.id;
    webApp.setHeaderColor("#F5F7F6");
    sessionStorage.setItem("tg_id", telegramId);

    console.log("⏳ Запускаем фоновое обновление после рендера...");

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        console.log("🔄 Фоновое обновление (requestIdleCallback)");
        refreshDataInBackground();
      }, {timeout: 500});
    } else {
      setTimeout(() => {
        console.log("🔄 Фоновое обновление (setTimeout)");
        refreshDataInBackground();
      }, 100);
    }

    // ✅ Подгружаем устройства, чтобы счётчики на кнопках были сразу
    fetchDevices();

    if (isNewUser.value) {
      console.log("👋 Новый пользователь, показываем Welcome Popup");
      showWelcomePopup.value = true;
      sendMetric('WelcomePopup-Shown');
    } else {
      console.log("👤 Существующий пользователь, пропускаем Welcome Popup");
    }
  } else {
    router.push('/welcome');
  }
});

onUnmounted(() => {
  stopBannerAutoplay();
  window.removeEventListener('resize', () => updateBannerOffset(false));
});

/* ===== Redesign additions (new UI state & upgrade flow) ===== */
const userName = ref('друг');

const toast = ref({ show: false, msg: '' });
let toastTimer = null;
const showToast = (msg) => {
  toast.value = { show: true, msg };
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value = { ...toast.value, show: false }; }, 2200);
};

const TARIFF_MONTHLY = { Basic: 149, Medium: 249, Premium: 399 };
const TARIFF_RANK = { Basic: 1, Medium: 2, Premium: 3 };
const WL_MONTH = 100;

const tariffKeyFromName = (name) => {
  if (!name) return null;
  if (name.includes('Premium')) return 'Premium';
  if (name.includes('Medium')) return 'Medium';
  if (name.includes('Basic')) return 'Basic';
  return null;
};
const nextTariffKey = (key) => {
  const order = ['Basic', 'Medium', 'Premium'];
  const i = order.indexOf(key);
  return (i >= 0 && i < order.length - 1) ? order[i + 1] : null;
};
const nextTariffName = (name) => {
  const key = tariffKeyFromName(name);
  return key ? nextTariffKey(key) : null;
};

const upgradeOpen = ref(false);
const upgradeFromKey = ref('Medium');
const upgradeToKey = ref('Premium');
const upgradeBypass = ref(false);
const upgradeRemainingDays = ref(18);
const upgradeLoading = ref(false);
let upgradeTariffIndex = -1;

// Пропорция считается по фактическому остатку дней (без верхнего ограничения):
// portion = дни / 30  (т.е. цена за месяц × количество месяцев остатка)
const clampDays = (d) => Math.max(1, Math.floor(d || 1));
const emojiByKey = (k) => k === 'Premium' ? '💎' : k === 'Medium' ? '🚀' : '⚡';

const upgradeProration = computed(() => {
  const portion = clampDays(upgradeRemainingDays.value) / 30;
  const from = TARIFF_MONTHLY[upgradeFromKey.value] || 0;
  const to = TARIFF_MONTHLY[upgradeToKey.value] || 0;
  const base = Math.round((to - from) * portion);
  const bypass = upgradeBypass.value ? Math.round(WL_MONTH * portion) : 0;
  return { base, bypass, total: base + bypass };
});
const upgradeBypassSurcharge = computed(() => Math.round(WL_MONTH * clampDays(upgradeRemainingDays.value) / 30));
const upgradeCanAfford = computed(() => (balance.value ?? 0) >= upgradeProration.value.total);
const upgradeAfterBalance = computed(() => Math.max(0, (balance.value ?? 0) - upgradeProration.value.total));

const openUpgrade = (tariff) => {
  haptic.impact();
  const key = tariffKeyFromName(tariff.name);
  const next = key ? nextTariffKey(key) : null;
  if (!next) { showToast('У вас максимальный тариф'); return; }
  upgradeFromKey.value = key;
  upgradeToKey.value = next;
  upgradeBypass.value = false;
  upgradeRemainingDays.value = clampDays(tariff.daysRemaining);
  upgradeTariffIndex = uniqueTariffs.value.indexOf(tariff);
  upgradeOpen.value = true;
  sendMetric(`OpenUpgrade-${key}`);
};
const closeUpgrade = () => { upgradeOpen.value = false; };
const toggleUpgradeBypass = () => { upgradeBypass.value = !upgradeBypass.value; };

const confirmUpgrade = async () => {
  const { total } = upgradeProration.value;
  if (TARIFF_RANK[upgradeToKey.value] <= TARIFF_RANK[upgradeFromKey.value]) { showToast('Некорректный тариф'); return; }
  if (!upgradeCanAfford.value) { showToast('Недостаточно средств на балансе'); return; }
  upgradeLoading.value = true;
  try {
    const res = await fetch(`${API_BASE}/upgradeTariff`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: buildAuthHeader()
      },
      body: JSON.stringify({
        from_tariff: upgradeFromKey.value.toLowerCase(),
        to_tariff: upgradeToKey.value.toLowerCase(),
        with_bypass: upgradeBypass.value
      })
    });
    if (res.status === 200) {
      const newBal = Math.max(0, (balance.value ?? 0) - total);
      balance.value = newBal;
      sessionStorage.setItem('balance', String(newBal));
      if (upgradeTariffIndex >= 0 && uniqueTariffs.value[upgradeTariffIndex]) {
        uniqueTariffs.value[upgradeTariffIndex].name = addEmojiToTariff(upgradeToKey.value);
      }
      upgradeOpen.value = false;
      showToast(`Тариф улучшен до ${upgradeToKey.value} · −${total} ₽`);
      sendMetric(`Upgrade-Success-${upgradeToKey.value}`);
    } else if (res.status === 400) { showToast('Некорректный тариф'); }
    else if (res.status === 402) { showToast('Недостаточно средств на балансе'); }
    else if (res.status === 404) { showToast('Профиль не найден'); }
    else if (res.status === 406) { showToast('Текущий тариф не активен'); }
    else { showToast('Не удалось улучшить тариф'); }
  } catch (e) {
    console.error('Upgrade error', e);
    showToast('Ошибка сети, попробуйте позже');
  } finally {
    upgradeLoading.value = false;
  }
};

onMounted(() => {
  try {
    const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
    if (tgUser?.first_name) userName.value = tgUser.first_name;
  } catch (e) { /* noop */ }
});

onUnmounted(() => { clearTimeout(toastTimer); });
</script>

<style scoped>
.home {
  animation: scrIn .32s ease;
  padding: 22px 18px 120px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
}
/* Prevent flexbox from shrinking cards inside the scroll container */
.home > * { flex-shrink: 0; }
@keyframes scrIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

/* Header */
.home-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.hi-wrap { min-width: 0; flex: 1; }
.hi-title { font-size: 22px; font-weight: 800; letter-spacing: -.02em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--ink); }
.hi-sub { font-size: 13px; color: var(--text-muted); margin-top: 2px; }
.avatar-btn {
  width: 46px; height: 46px; border-radius: 15px; background: #fff; border: 1px solid var(--border-2);
  display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 10px rgba(16,24,20,.05);
  cursor: pointer; flex-shrink: 0; transition: transform .12s;
}
.avatar-btn:active { transform: scale(.94); }

/* Balance */
.balance-card {
  background: var(--ink); border-radius: 22px; padding: 20px; position: relative; overflow: hidden;
  box-shadow: 0 12px 30px rgba(15,21,18,.22);
}
.balance-glow { position: absolute; right: -40px; top: -40px; width: 150px; height: 150px; border-radius: 50%; background: radial-gradient(circle, rgba(18,183,106,.45), transparent 70%); }
.balance-top { display: flex; align-items: center; justify-content: space-between; position: relative; }
.balance-label { font-size: 13px; color: rgba(255,255,255,.6); font-weight: 600; }
.history-btn { border: none; cursor: pointer; width: 34px; height: 34px; border-radius: 10px; background: rgba(255,255,255,.1); color: #fff; display: flex; align-items: center; justify-content: center; transition: transform .12s; }
.history-btn:active { transform: scale(.9); }
.balance-amount { font-size: 38px; font-weight: 800; letter-spacing: -.03em; color: #fff; margin: 8px 0 16px; position: relative; }
.balance-amount .rub { font-size: 22px; color: rgba(255,255,255,.5); }
.balance-cta {
  width: 100%; border: none; cursor: pointer; padding: 14px; border-radius: 14px; background: var(--green); color: #fff;
  font-family: inherit; font-size: 15px; font-weight: 800; box-shadow: 0 8px 20px rgba(18,183,106,.36);
  transition: transform .12s; display: flex; align-items: center; justify-content: center; gap: 8px; position: relative;
}
.balance-cta:active { transform: scale(.98); }

/* Banners */
.banners-section { border-radius: 20px; }
.banners-container { width: 100%; overflow: hidden; border-radius: 20px; position: relative; }
.banners-track { display: flex; transition: transform .35s cubic-bezier(.4,0,.2,1); will-change: transform; }
.banner-item { min-width: 100%; cursor: pointer; user-select: none; -webkit-user-select: none; }
.banner-image { width: 100%; height: auto; display: block; border-radius: 20px; object-fit: cover; aspect-ratio: 1280/442; background: var(--chip-bg); }
.banner-dots { position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%); display: flex; gap: 7px; z-index: 10; padding: 6px 10px; background: rgba(15,21,18,.25); backdrop-filter: blur(8px); border-radius: 20px; }
.dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(255,255,255,.6); cursor: pointer; transition: all .3s ease; }
.dot.active { background: #fff; width: 18px; border-radius: 3px; }

/* Subscription card */
.sub-card { background: #fff; border: 1px solid var(--border); border-radius: 22px; padding: 16px; box-shadow: 0 4px 16px rgba(16,24,20,.05); }
.sub-skeleton { height: 190px; background: linear-gradient(100deg, #f1f4f2 30%, #f8faf9 50%, #f1f4f2 70%); background-size: 200% 100%; animation: shimmerBg 1.3s infinite; }
@keyframes shimmerBg { to { background-position: -200% 0; } }
.sub-inner { border: 1.5px solid var(--border); border-radius: 18px; padding: 16px; background: var(--surface-alt); }
.sub-row { display: flex; align-items: center; justify-content: space-between; }
.sub-title { font-size: 18px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); }
.badge-active { font-size: 11px; font-weight: 800; color: var(--green-dark); background: var(--success-bg); padding: 5px 11px; border-radius: 999px; }
.badge-active.expired { color: #B4890B; background: #FEF3D3; }
.chips { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 12px; }
.chip { font-size: 12px; font-weight: 600; color: #4B5651; background: var(--chip-bg); border: 1px solid var(--border-3); padding: 5px 10px; border-radius: 9px; }
.progress-wrap { margin-top: 14px; }
.progress-track { height: 8px; border-radius: 999px; background: var(--track-bg); overflow: hidden; }
.progress-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg, var(--green), var(--green-dark)); transition: width .4s ease; }
.progress-label { font-size: 12px; color: var(--text-muted); font-weight: 600; margin-top: 7px; }
.expired-note { font-size: 13px; color: var(--text-muted); font-weight: 600; margin-top: 12px; }
.sub-actions { display: flex; gap: 9px; margin-top: 14px; }
.btn-connect {
  flex: 1; border: none; cursor: pointer; padding: 13px; border-radius: 13px; background: var(--green); color: #fff;
  font-family: inherit; font-size: 14px; font-weight: 800; box-shadow: 0 6px 16px rgba(18,183,106,.3);
  transition: transform .12s; display: flex; align-items: center; justify-content: center; gap: 7px;
}
.btn-connect:active { transform: scale(.98); }
.btn-devices {
  border: 1px solid #DAE4DF; cursor: pointer; padding: 13px 15px; border-radius: 13px; background: #fff; color: var(--ink);
  font-family: inherit; font-size: 14px; font-weight: 700; transition: transform .12s; display: flex; align-items: center; gap: 7px; white-space: nowrap;
}
.btn-devices:active { transform: scale(.98); }
.btn-upgrade {
  width: 100%; margin-top: 9px; border: 1px dashed rgba(18,183,106,.5); cursor: pointer; padding: 12px; border-radius: 13px;
  background: var(--mint-bg); color: var(--green-dark); font-family: inherit; font-size: 13.5px; font-weight: 800;
  transition: transform .12s; display: flex; align-items: center; justify-content: center; gap: 7px;
}
.btn-upgrade:active { transform: scale(.98); }
.btn-upgrade.max { border-color: #D8DEDA; background: var(--app-bg); color: var(--text-faint); }

/* Empty state */
.empty-state { background: #fff; border: 1px solid var(--border); border-radius: 22px; text-align: center; padding: 40px 20px; box-shadow: 0 4px 16px rgba(16,24,20,.05); }
.empty-icon { font-size: 44px; }
.empty-title { font-size: 18px; font-weight: 800; margin-top: 10px; color: var(--ink); }
.empty-text { font-size: 14px; color: var(--text-muted); margin: 6px 0 18px; }
.btn-buy { border: none; cursor: pointer; padding: 15px 24px; border-radius: 14px; background: var(--green); color: #fff; font-family: inherit; font-size: 15px; font-weight: 800; box-shadow: 0 8px 20px rgba(18,183,106,.32); transition: transform .12s; }
.btn-buy:active { transform: scale(.98); }

/* Legal */
.legal-links { display: flex; justify-content: center; align-items: center; gap: 8px; font-size: 11px; color: var(--text-faint); margin-top: 2px; flex-wrap: wrap; }
.legal-links a { color: var(--text-faint); text-decoration: none; }
.legal-links .separator { color: var(--hairline); }

/* ===== Bottom sheets (liquid glass) ===== */
.sheet-overlay { position: fixed; inset: 0; z-index: 10001; display: flex; align-items: flex-end; justify-content: center; background: rgba(15,21,18,.5); backdrop-filter: blur(5px); -webkit-backdrop-filter: blur(5px); animation: fadeIn .2s ease; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.sheet {
  width: 100%; max-width: 480px; background: rgba(255,255,255,.9); backdrop-filter: blur(30px) saturate(180%); -webkit-backdrop-filter: blur(30px) saturate(180%);
  border-top: 1px solid rgba(255,255,255,.9); border-radius: 28px 28px 0 0; padding: 22px 20px calc(22px + env(safe-area-inset-bottom));
  box-shadow: 0 -12px 50px rgba(0,0,0,.28), inset 0 1px 0 rgba(255,255,255,.9); animation: sheetIn .32s cubic-bezier(.16,1,.3,1); max-height: 88vh; overflow-y: auto;
}
@keyframes sheetIn { from { opacity: 0; transform: translateY(40px) scale(.98); } to { opacity: 1; transform: none; } }
.sheet-handle { width: 40px; height: 4px; border-radius: 999px; background: #E2E7E4; margin: 0 auto 14px; }
.sheet-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.sheet-title { font-size: 18px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); }
.sheet-sub { font-size: 13px; color: var(--text-muted); margin-top: 3px; margin-bottom: 8px; }
.sheet-close { width: 32px; height: 32px; border-radius: 50%; border: none; cursor: pointer; background: var(--chip-bg2); color: var(--text-muted); display: flex; align-items: center; justify-content: center; }
.devices-loading, .devices-empty { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 30px 0; color: var(--text-muted); font-size: 14px; font-weight: 600; text-align: center; }
.devices-list { display: flex; flex-direction: column; gap: 10px; }
.device-card { display: flex; align-items: center; gap: 12px; background: var(--surface-alt2); border: 1px solid var(--chip-bg); border-radius: 15px; padding: 13px 14px; }
.device-icon { width: 40px; height: 40px; border-radius: 12px; background: #fff; border: 1px solid var(--border-3); display: flex; align-items: center; justify-content: center; font-size: 19px; flex-shrink: 0; }
.device-info { flex: 1; min-width: 0; }
.device-model { font-size: 14px; font-weight: 700; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.device-meta { font-size: 11.5px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.device-actions { display: flex; gap: 8px; flex-shrink: 0; }
.dev-del, .dev-yes, .dev-no { width: 36px; height: 36px; border-radius: 11px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: transform .12s; }
.dev-del { background: var(--danger-bg); color: var(--danger); }
.dev-yes { background: var(--danger); color: #fff; }
.dev-no { background: var(--chip-bg2); color: var(--text-muted); }
.dev-del:active, .dev-yes:active, .dev-no:active { transform: scale(.9); }
.sheet-hint { font-size: 11.5px; color: var(--text-faint); text-align: center; margin-top: 14px; line-height: 1.4; }

/* Upgrade sheet */
.up-current { display: flex; align-items: center; gap: 10px; margin-top: 16px; padding: 12px 14px; background: var(--chip-bg2); border: 1px solid var(--border-3); border-radius: 14px; }
.up-emoji { font-size: 20px; }
.up-current-info { flex: 1; }
.up-label { font-size: 12px; color: var(--text-muted); font-weight: 600; }
.up-name { font-size: 15px; font-weight: 800; color: var(--ink); }
.up-arrow { color: var(--hairline); font-size: 20px; }
.up-option { width: 100%; margin-top: 12px; border: 1.5px solid var(--green); background: rgba(18,183,106,.08); cursor: pointer; padding: 15px; border-radius: 16px; display: flex; align-items: center; gap: 12px; font-family: inherit; text-align: left; }
.up-option-info { flex: 1; }
.up-option-name { font-size: 16px; font-weight: 800; color: var(--ink); }
.up-option-name.sm { font-size: 14px; font-weight: 700; }
.up-option-desc { font-size: 12px; color: var(--text-muted); }
.up-option-price { text-align: right; }
.up-surcharge { font-size: 15px; font-weight: 800; color: var(--green); }
.up-surcharge-label { font-size: 11px; color: var(--text-faint); font-weight: 600; }
.up-bypass { cursor: pointer; margin-top: 12px; display: flex; align-items: center; gap: 12px; padding: 14px 15px; background: #fff; border: 1px solid var(--border); border-radius: 15px; }
.toggle { width: 46px; height: 27px; border-radius: 999px; background: #D8DEDA; position: relative; transition: background .25s ease; flex-shrink: 0; }
.toggle.on { background: var(--green); }
.toggle .knob { position: absolute; top: 3px; left: 3px; width: 21px; height: 21px; border-radius: 50%; background: #fff; box-shadow: 0 2px 5px rgba(0,0,0,.2); transition: transform .25s cubic-bezier(.34,1.42,.5,1); }
.toggle.on .knob { transform: translateX(19px); }
.up-summary { margin-top: 16px; padding: 15px; background: var(--surface-alt2); border: 1px solid var(--chip-bg); border-radius: 16px; }
.up-summary-row { display: flex; align-items: center; justify-content: space-between; font-size: 13px; color: var(--text-muted); font-weight: 600; }
.up-summary-row b { font-size: 20px; font-weight: 800; letter-spacing: -.02em; color: var(--ink); }
.up-summary-row.muted { margin-top: 8px; }
.up-summary-row.muted span { font-size: 12px; color: var(--text-faint); }
.up-summary-row.muted b { font-size: 13px; font-weight: 700; color: var(--ink); }
.up-summary-row b.danger { color: var(--danger); }
.up-cta { width: 100%; margin-top: 14px; border: none; cursor: pointer; padding: 16px; border-radius: 15px; background: var(--green); color: #fff; font-family: inherit; font-size: 16px; font-weight: 800; box-shadow: 0 10px 24px rgba(18,183,106,.32); transition: transform .12s; display: flex; align-items: center; justify-content: center; }
.up-cta:active { transform: scale(.98); }
.up-cta.disabled { background: #B7C4BD; box-shadow: none; }

/* Onboarding (kept) */
.onboarding-overlay { position: fixed; inset: 0; background: rgba(15,21,18,.75); z-index: 9998; backdrop-filter: blur(4px); animation: fadeIn .3s ease; }
.onboarding-spotlight { position: absolute; background: transparent; border-radius: 18px; box-shadow: 0 0 0 9999px rgba(15,21,18,.75); z-index: 9999; pointer-events: none; transition: all .3s ease; }
.onboarding-tooltip { position: fixed; background: linear-gradient(135deg, var(--green), var(--green-dark)); border-radius: 20px; padding: 22px; z-index: 10000; max-width: 320px; box-shadow: 0 20px 50px rgba(18,183,106,.4); }
.tooltip-content { text-align: center; }
.tooltip-icon { font-size: 42px; margin-bottom: 10px; }
.tooltip-title { font-size: 19px; font-weight: 800; color: #fff; margin-bottom: 6px; }
.tooltip-text { font-size: 14px; color: rgba(255,255,255,.95); line-height: 1.5; margin-bottom: 14px; }
.tooltip-btn { background: #fff; color: var(--green-dark); padding: 12px 24px; border-radius: 12px; font-size: 15px; font-weight: 800; border: none; cursor: pointer; width: 100%; }

/* Toast */
.toast { position: fixed; top: 16px; left: 50%; transform: translateX(-50%); z-index: 10002; max-width: 400px; width: calc(100% - 32px); padding: 14px 18px; border-radius: 14px; font-size: 14px; font-weight: 700; text-align: center; box-shadow: 0 14px 36px rgba(0,0,0,.2); background: var(--green); color: #fff; animation: toastIn .28s ease; }
@keyframes toastIn { from { opacity: 0; transform: translate(-50%,-16px); } to { opacity: 1; transform: translate(-50%,0); } }

/* Spinners & skeleton */
.spinner { width: 20px; height: 20px; border: 2px solid rgba(18,183,106,.2); border-top-color: var(--green); border-radius: 50%; animation: spin .8s linear infinite; }
.spinner-sm { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.5); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; display: inline-block; }
@keyframes spin { to { transform: rotate(360deg); } }
.balance-amount.skeleton { color: transparent; background: rgba(255,255,255,.12); border-radius: 8px; width: 140px; }
button:disabled { opacity: .6; cursor: not-allowed; }
</style>
